import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Eye, Ear, Wrench } from "lucide-react";
import { TYPE_STYLE } from "@/components/app/BlockStakeholders";

const H = ({ children }) => <h2 className="font-head text-xl font-extrabold tracking-tight text-foreground">{children}</h2>;

export function Bullets({ s }) {
  return (
    <section>
      {s.title && <H>{s.title}</H>}
      {s.note && <p className="text-sm text-muted-foreground mt-1">{s.note}</p>}
      <ul className="mt-3 space-y-2">{s.items.map((it, i) => <li key={i} className="flex gap-2.5 text-body leading-relaxed"><span className="mt-2 w-1.5 h-1.5 rounded-full bg-accent-500 shrink-0" /><span>{it}</span></li>)}</ul>
    </section>
  );
}

export function Stages({ s, stages }) {
  return (
    <section data-testid="change-curve-stages">
      <H>{s.title}</H>
      {s.note && <p className="text-sm text-muted-foreground mt-1">{s.note}</p>}
      <div className="mt-4 space-y-4">
        {stages.map((st, i) => (
          <div key={st.name} className="pp-card p-5" data-testid={`stage-${st.name.toLowerCase()}`}>
            <div className="flex items-center gap-3 flex-wrap"><span className="w-7 h-7 rounded-full bg-ink text-on-dark text-xs font-bold flex items-center justify-center">{i + 1}</span><div className="font-head text-lg font-extrabold">{st.name}</div>{st.model_stages.length > 0 && <span className="text-xs text-muted-foreground">Kübler-Ross: {st.model_stages.join(", ")}</span>}</div>
            <div className="grid md:grid-cols-3 gap-4 mt-4 text-sm">
              <div><div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-muted-foreground"><Eye size={12} /> You see</div><ul className="mt-2 space-y-1 text-body">{st.see.map((x) => <li key={x}>{x}</li>)}</ul></div>
              <div><div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-muted-foreground"><Ear size={12} /> You hear</div><ul className="mt-2 space-y-1 text-body italic">{st.hear.map((x) => <li key={x}>{x}</li>)}</ul></div>
              <div className="md:border-l md:border-border-soft md:pl-4"><div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-accent-700"><Wrench size={12} /> Strategies</div><ul className="mt-2 space-y-1.5 text-foreground">{st.strategies.map((x) => <li key={x} className="flex gap-2"><span className="mt-2 w-1 h-1 rounded-full bg-accent-500 shrink-0" />{x}</li>)}</ul></div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function Table({ s }) {
  return (
    <section>
      {s.title && <H>{s.title}</H>}
      <div className="mt-3 pp-card overflow-x-auto thin-scroll">
        <table className="w-full text-xs sm:text-sm min-w-[640px]">
          <thead className="bg-muted text-[11px] uppercase tracking-wide text-muted-foreground"><tr>{s.columns.map((c) => <th key={c} className="text-left font-bold px-3 py-2.5">{c}</th>)}</tr></thead>
          <tbody className="divide-y divide-border-soft">{s.rows.map((r, i) => <tr key={i} className="align-top">{r.map((cell, j) => <td key={j} className={`px-3 py-2.5 leading-relaxed ${j === 0 ? "font-bold text-foreground whitespace-nowrap" : "text-body"}`}>{cell}</td>)}</tr>)}</tbody>
        </table>
      </div>
    </section>
  );
}

export function ValuesExercise({ s, values }) {
  const [picked, setPicked] = useState([]);
  const [text, setText] = useState("");
  const toggle = (v) => setPicked(picked.includes(v) ? picked.filter((x) => x !== v) : picked.length < 5 ? [...picked, v] : picked);
  return (
    <section data-testid="values-exercise">
      <H>{s.title}</H>
      <p className="text-sm text-muted-foreground mt-1">Tick five. Add any that are missing that matter to you. Nothing here is saved or shared — it's for you.</p>
      <div className="flex flex-wrap gap-2 mt-4">{values.map((v) => <button key={v} type="button" data-testid={`value-${v.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => toggle(v)} className={`rounded-full border px-3 py-1 text-xs font-bold transition-colors ${picked.includes(v) ? "bg-ink text-on-dark border-ink" : picked.length >= 5 ? "bg-card text-faint border-border" : "bg-card text-body border-border hover:border-accent-400"}`}>{v}</button>)}</div>
      <div className="text-xs text-muted-foreground mt-2" data-testid="values-count">{picked.length}/5 chosen{picked.length === 5 ? " — that's the set." : ""}</div>
      {picked.length > 0 && (
        <div className="pp-card p-5 mt-4 bg-muted" data-testid="values-question">
          <div className="text-sm font-bold text-foreground">{s.question}</div>
          <div className="flex flex-wrap gap-1.5 mt-2">{picked.map((v) => <span key={v} className="rounded-full bg-card border border-border px-2.5 py-0.5 text-xs font-semibold">{v}</span>)}</div>
          <textarea data-testid="values-answer" className="pp-input mt-3 min-h-[90px]" value={text} onChange={(e) => setText(e.target.value)} placeholder="e.g. Fairness — the current process makes field staff lend the company money for three weeks. Fixing that is something I can stand behind." />
        </div>
      )}
    </section>
  );
}

export function ComponentsMap({ components, tabs, changeId, compact = false }) {
  // With a change in context the fix link opens that change's tab; without one it opens the Learn article for the component (the list page has no tabs).
  const to = (c) => (changeId ? `/app/changes/${changeId}?tab=${c.tab}` : `/app/learn/${c.learn}`);
  const label = (c) => (changeId ? tabs[c.tab] : "How to fix it");
  return (
    <div className="space-y-2" data-testid="components-map">
      {components.map((c) => (
        <div key={c.key} data-testid={`component-${c.key}`} className={`grid ${compact ? "grid-cols-[1fr_auto]" : "sm:grid-cols-[.9fr_1fr_1.1fr_auto]"} gap-x-4 gap-y-1 items-center rounded-xl border border-border px-3 py-2.5 text-sm`}>
          <div><span className="inline-block rounded bg-danger-50 text-danger-700 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 mr-2 align-middle">Missing</span><span className="font-bold text-foreground">{c.name}</span>{compact && <span className="text-muted-foreground"> → {c.consequence}</span>}</div>
          {!compact && <div className="text-danger-700 font-semibold">→ {c.consequence}</div>}
          {!compact && <div className="text-xs text-muted-foreground">{c.remedies.join(" · ")}</div>}
          <Link to={to(c)} data-testid={`component-fix-${c.key}`} aria-label={`${label(c)} — ${c.name}`} className="inline-flex items-center gap-1 text-xs font-bold text-accent-700 hover:text-accent-900 whitespace-nowrap">{label(c)} <ArrowRight size={11} /></Link>
        </div>
      ))}
    </div>
  );
}

export function Approaches({ types, order }) {
  return (
    <section data-testid="approaches" className="space-y-3">
      {order.map((k) => { const t = types[k]; return (
        <div key={k} className={`rounded-xl border p-4 ${TYPE_STYLE[k]}`} data-testid={`approach-${k}`}>
          <div className="flex items-center justify-between flex-wrap gap-2"><div className="font-head font-extrabold">{t.name} <span className="text-xs font-semibold opacity-70">· {t.agreement} agreement · {t.trust} trust</span></div><span className="text-xs font-bold">Goal: {t.goal}</span></div>
          <ol className="mt-2 space-y-1 text-sm">{t.steps.map((st, i) => <li key={i} className="flex gap-2"><span className="font-mono text-xs opacity-60 w-4 shrink-0">{i + 1}.</span><span>{st}</span></li>)}</ol>
        </div>); })}
    </section>
  );
}

export function Phases({ s }) {
  return (
    <section>
      <H>{s.title}</H>
      <div className="grid md:grid-cols-3 gap-3 mt-3">{Object.entries(s.phases).map(([name, items]) => <div key={name} className="pp-card p-4"><div className="text-xs font-bold uppercase tracking-widest text-accent-700">{name}</div><ul className="mt-2 space-y-1.5 text-sm text-body">{items.map((it, i) => <li key={i} className="flex gap-2"><span className="mt-2 w-1 h-1 rounded-full bg-border-strong shrink-0" />{it}</li>)}</ul></div>)}</div>
    </section>
  );
}

export function ToolLink({ s, changeId }) {
  return (
    <section className="pp-card p-5 bg-ink text-on-dark" data-testid={`tool-${s.tab}`}>
      <div className="text-xs font-bold uppercase tracking-widest text-brand-300">In ChangeOS</div>
      <div className="font-head text-lg font-extrabold mt-1">{s.title}</div>
      <p className="text-sm text-faint mt-1 leading-relaxed">{s.text}</p>
      <Link to={changeId ? `/app/changes/${changeId}?tab=${s.tab}` : `/app/changes?tab=${s.tab}`} className="pp-btn-dark bg-card text-foreground hover:bg-brand-300 text-xs py-1.5 mt-3">{changeId ? "Open on this change" : "Pick a change"} <ArrowRight size={12} /></Link>
    </section>
  );
}
